import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logoAms from "@/assets/logo-ams-transparent.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { href: "/", label: "Accueil" },
    { href: "/produits", label: "Produits" },
    { href: "/valeurs", label: "Nos Valeurs" },
    { href: "/certifications", label: "Certifications" },
  ];

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Skip to content link for accessibility */}
      <a 
        href="#main-content" 
        className="skip-link sr-only focus:not-sr-only"
      >
        Aller au contenu principal
      </a>

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-background/98 backdrop-blur-xl shadow-lg py-2"
            : "bg-background/80 backdrop-blur-sm py-4"
        }`}
        role="banner"
      >
        {/* Top bar with phone - visible on scroll */}
        <AnimatePresence>
          {isScrolled && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="hidden lg:block border-b border-border/50 -mt-2 mb-2"
            >
              <div className="container mx-auto px-4 py-1.5 flex justify-end items-center gap-4 text-xs text-muted-foreground">
                <a 
                  href="tel:+33611416100" 
                  className="flex items-center gap-1.5 hover:text-primary transition-colors"
                  aria-label="Appeler le 06 11 41 61 00"
                >
                  <Phone className="w-3 h-3" aria-hidden="true" />
                  06 11 41 61 00
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-3 group"
              aria-label="AMS DISTRIB - Retour à l'accueil"
            >
              <img 
                src={logoAms} 
                alt="AMS DISTRIB - Emballage alimentaire durable" 
                className="h-16 w-auto object-contain group-hover:scale-105 transition-transform duration-300 drop-shadow-md"
                width="160"
                height="64"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" aria-label="Navigation principale">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`relative text-sm font-medium transition-colors duration-300 py-2 ${
                    isActive(link.href)
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  aria-current={isActive(link.href) ? "page" : undefined}
                >
                  {link.label}
                  {isActive(link.href) && (
                    <motion.div
                      layoutId="activeNav"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full"
                    />
                  )}
                </Link>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden md:block">
              <Link to="/devis">
                <Button variant="kraft" size="lg">
                  Demander un devis
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-foreground rounded-lg hover:bg-secondary transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            >
              {isMobileMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              id="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-background/95 backdrop-blur-lg border-t border-border"
            >
              <nav className="container mx-auto px-4 py-6 flex flex-col gap-4" aria-label="Navigation mobile">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`text-base font-medium py-3 px-4 rounded-lg transition-colors ${
                      isActive(link.href)
                        ? "text-primary bg-primary/5"
                        : "text-muted-foreground hover:bg-secondary"
                    }`}
                    aria-current={isActive(link.href) ? "page" : undefined}
                  >
                    {link.label}
                  </Link>
                ))}
                
                {/* Mobile phone */}
                <a 
                  href="tel:+33611416100" 
                  className="flex items-center gap-2 text-muted-foreground py-3 px-4 rounded-lg hover:bg-secondary transition-colors"
                >
                  <Phone className="w-4 h-4" aria-hidden="true" />
                  06 11 41 61 00
                </a>

                <Link to="/devis">
                  <Button variant="kraft" className="w-full mt-2" size="lg">
                    Demander un devis
                  </Button>
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;